import { create } from 'zustand';
import { ConnectorConfig, EdgeLiveness } from '../types/connector';
import { getAccessToken, getTenantId, getUserId } from './authStore';

// ─── Store ──────────────────────────────────────────────────────────────────

const API = import.meta.env.VITE_CONNECTOR_SERVICE_URL || 'http://localhost:8001';
const PIPELINE_API = import.meta.env.VITE_PIPELINE_SERVICE_URL || 'http://localhost:8002';

interface ConnectorStoreState {
  connectors: ConnectorConfig[];
  loading: boolean;
  error: string | null;
  fetchConnectors: () => Promise<void>;
  addConnector: (req: Omit<ConnectorConfig, 'id' | 'createdAt' | 'updatedAt' | 'tenantId' | 'activePipelineCount'>) => Promise<ConnectorConfig>;
  updateConnector: (id: string, updates: Partial<ConnectorConfig>) => Promise<void>;
  removeConnector: (id: string) => Promise<void>;
}

export const useConnectorStore = create<ConnectorStoreState>((set) => ({
  connectors: [],
  loading: false,
  error: null,

  fetchConnectors: async () => {
    set({ loading: true, error: null });
    try {
      const userId = getUserId();
      const baseHeaders: Record<string, string> = { 'x-tenant-id': getTenantId() };
      if (userId) baseHeaders['x-user-id'] = userId;
      const [connRes, pipeRes] = await Promise.all([
        fetch(`${API}/connectors`, { headers: baseHeaders }),
        fetch(`${PIPELINE_API}/pipelines`, { headers: { 'x-tenant-id': getTenantId() } }).catch(() => null),
      ]);
      if (!connRes.ok) throw new Error(`HTTP ${connRes.status}`);
      const data = await connRes.json();

      // Build per-connector pipeline stats from the pipeline service
      const pipelineStats: Record<string, { count: number; lastRun: string | null }> = {};
      if (pipeRes?.ok) {
        const pipes: Record<string, unknown>[] = await pipeRes.json();
        for (const p of pipes) {
          const ids = (p.connector_ids as string[]) ?? [];
          const lastRun = (p.last_run_at as string | null) ?? null;
          for (const cid of ids) {
            if (!pipelineStats[cid]) pipelineStats[cid] = { count: 0, lastRun: null };
            pipelineStats[cid].count += 1;
            if (lastRun && (!pipelineStats[cid].lastRun || lastRun > pipelineStats[cid].lastRun!)) {
              pipelineStats[cid].lastRun = lastRun;
            }
          }
        }
      }

      // Normalise snake_case from API to camelCase used by frontend types
      const connectors: ConnectorConfig[] = data.map((c: Record<string, unknown>) => {
        const stats = pipelineStats[c.id as string];
        return {
          id: c.id,
          name: c.name,
          type: c.type,
          category: c.category,
          status: c.status,
          description: c.description,
          baseUrl: c.base_url,
          authType: c.auth_type,
          credentials: (c.config_metadata || c.credentials) as Record<string, string> | undefined,
          headerValuesSet: c.header_values_set as Record<string, boolean> | undefined,
          paginationStrategy: c.pagination_strategy,
          activePipelineCount: stats?.count ?? (c.active_pipeline_count as number) ?? 0,
          lastSync: stats?.lastRun ?? (c.last_sync as string | null) ?? undefined,
          lastSyncRowCount: c.last_sync_row_count,
          schemaHash: c.schema_hash,
          tags: c.tags ?? [],
          config: c.config,
          createdAt: c.created_at,
          updatedAt: c.updated_at,
          tenantId: c.tenant_id,
          visibility: (c.visibility as 'tenant' | 'private') ?? 'tenant',
          createdBy: c.created_by as string | undefined,
          vpnProfileId: (c.vpn_profile_id as string | null) ?? undefined,
          agentId: (c.agent_id as string | null) ?? undefined,
          sslCaCertInfo: (c.ssl_ca_cert_info as ConnectorConfig['sslCaCertInfo']) ?? null,
          transportUnencrypted: (c.transport_unencrypted as boolean | null) ?? null,
          insecureTransportAcknowledged:
            (c.insecure_transport_acknowledged as ConnectorConfig['insecureTransportAcknowledged']) ?? null,
          // EDGE liveness travels as one opaque snake_case block: null for every
          // other connector type, which is how the UI tells them apart.
          edge: (c.edge as EdgeLiveness | null) ?? null,
        };
      });
      set({ connectors, loading: false });
    } catch (err: unknown) {
      set({ error: String(err), loading: false });
    }
  },

  addConnector: async (req) => {
    const body = {
      name: req.name,
      type: req.type,
      category: req.category,
      description: req.description,
      base_url: req.baseUrl,
      auth_type: req.authType,
      credentials: req.credentials,
      pagination_strategy: req.paginationStrategy,
      tags: req.tags ?? [],
      config: req.config,
      visibility: req.visibility ?? 'tenant',
      vpn_profile_id: req.vpnProfileId ?? null,
      agent_id: req.agentId ?? null,
    };
    const createHeaders: Record<string, string> = { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() };
    const uid = getUserId();
    if (uid) createHeaders['x-user-id'] = uid;
    // An EDGE connector's tenant comes from the caller's own credential, never
    // from the tenant header, so creating one needs a bearer. The endpoint
    // takes it as an OPTIONAL header and reads it only on the EDGE branch, so
    // sending it changes nothing for the other connector types.
    const accessToken = getAccessToken();
    if (accessToken) createHeaders.Authorization = `Bearer ${accessToken}`;
    const res = await fetch(`${API}/connectors`, {
      method: 'POST',
      headers: createHeaders,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`Failed to create connector: ${detail}`);
    }
    const c = await res.json();
    const connector: ConnectorConfig = {
      id: c.id,
      name: c.name,
      type: c.type,
      category: c.category,
      status: c.status,
      description: c.description,
      baseUrl: c.base_url,
      authType: c.auth_type,
      credentials: (c.config_metadata || c.credentials) as Record<string, string> | undefined,
      headerValuesSet: c.header_values_set as Record<string, boolean> | undefined,
      paginationStrategy: c.pagination_strategy,
      activePipelineCount: c.active_pipeline_count ?? 0,
      lastSync: c.last_sync,
      lastSyncRowCount: c.last_sync_row_count,
      schemaHash: c.schema_hash,
      tags: c.tags ?? [],
      config: c.config,
      createdAt: c.created_at,
      updatedAt: c.updated_at,
      tenantId: c.tenant_id,
      visibility: (c.visibility as 'tenant' | 'private') ?? 'tenant',
      createdBy: c.created_by as string | undefined,
      vpnProfileId: (c.vpn_profile_id as string | null) ?? undefined,
      agentId: (c.agent_id as string | null) ?? undefined,
      sslCaCertInfo: c.ssl_ca_cert_info ?? null,
      transportUnencrypted: c.transport_unencrypted ?? null,
      insecureTransportAcknowledged: c.insecure_transport_acknowledged ?? null,
      edge: (c.edge as EdgeLiveness | null) ?? null,
    };
    set((state) => ({ connectors: [...state.connectors, connector] }));
    return connector;
  },

  updateConnector: async (id, updates) => {
    const body: Record<string, unknown> = {};
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.description !== undefined) body.description = updates.description;
    if (updates.baseUrl !== undefined) body.base_url = updates.baseUrl;
    if (updates.authType !== undefined) body.auth_type = updates.authType;
    if (updates.credentials !== undefined) body.credentials = updates.credentials;
    if (updates.tags !== undefined) body.tags = updates.tags;
    if (updates.paginationStrategy !== undefined) body.pagination_strategy = updates.paginationStrategy;
    if (updates.config !== undefined) body.config = updates.config;
    if (updates.headers !== undefined) body.headers = updates.headers;
    // `disabled` is the only operator-set status; the rest are the system's.
    // The endpoint constrains it to `active`/`disabled` and rejects anything
    // else, because a stored value that matches no predicate downstream leaves
    // a number receiving and sending while the UI reports it off.
    if (updates.status !== undefined) body.status = updates.status;
    // "" detaches the connector from its VPN profile; the backend reads an empty
    // string as "unset" because null already means "field not sent".
    if (updates.vpnProfileId !== undefined) body.vpn_profile_id = updates.vpnProfileId ?? '';
    // Same convention for the on-premise agent (CONN-4).
    if (updates.agentId !== undefined) body.agent_id = updates.agentId ?? '';
    if (updates.clearCredentials?.length) body.clear_credentials = updates.clearCredentials;
    const res = await fetch(`${API}/connectors/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-tenant-id': getTenantId() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // A 422 explains what to fix (e.g. a CA bundle that is not a certificate).
      let detail = '';
      try { const j = await res.json(); detail = typeof j?.detail === 'string' ? j.detail : ''; } catch { /* not JSON */ }
      throw new Error(detail ? `HTTP ${res.status}: ${detail}` : `HTTP ${res.status}`);
    }
    const c = await res.json();
    set((state) => ({
      connectors: state.connectors.map((conn) =>
        conn.id === id
          // `updates.config` is only the keys the caller owns — the server
          // merges it over the stored blob, so the response is the only
          // complete version. Keeping the partial here would leave the other
          // forms reading a config with everyone else's keys missing.
          ? {
              ...conn, ...updates, clearCredentials: undefined,
              config: c.config ?? conn.config, updatedAt: c.updated_at,
              sslCaCertInfo: c.ssl_ca_cert_info ?? null,
              transportUnencrypted: c.transport_unencrypted ?? null,
              insecureTransportAcknowledged: c.insecure_transport_acknowledged ?? null,
            }
          : conn
      ),
    }));
  },

  removeConnector: async (id) => {
    const res = await fetch(`${API}/connectors/${id}`, { method: 'DELETE', headers: { 'x-tenant-id': getTenantId() } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    set((state) => ({
      connectors: state.connectors.filter((c) => c.id !== id),
    }));
  },
}));
